import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  AddCircleRounded,
  ArrowDownward,
  ArrowUpward,
  BusinessRounded,
  CloseRounded,
  DeleteRounded,
  DescriptionRounded,
  EditRounded,
  FolderCopy,
  GroupsRounded,
  InfoRounded,
  SchoolRounded,
  Search,
  SettingsRounded,
  SubjectRounded,
  UnfoldMore,
  VisibilityRounded,
} from "@mui/icons-material";

import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";

import ErrorState from "@componentes/ErrorWindow";
import { api } from "@api/api";
import PaginacaoLista from "../../../componentes/comuns/PaginacaoLista";
import CriarProcesso from "./registrarTipoProcesso";
import { botaoAdicionar } from "../../../estilos/botoes";
import { ajustarTamanhoFonteSre } from "../../../estilos/global";

const OPCOES_ITENS_POR_PAGINA = [5, 10, 20];

// Categoria experimental. Ainda não é persistida no backend.
const EXIBIR_CATEGORIA_EXPERIMENTAL = process.env.NODE_ENV !== "production";

const ROTULOS_CATEGORIA = {
  escola: "Escola",
  mantenedora: "Mantenedora",
  sem_categoria: "Não definida",
};

const ESTILO_ACAO_EDITAR = {
  width: 30,
  height: 30,
  p: 0,
  color: "#087cf0",
  backgroundColor: "#eaf4ff",
  border: "1px solid #d7eaff",
  borderRadius: "50%",
  transition:
    "background-color 0.15s ease, color 0.15s ease, transform 0.15s ease",

  "&:hover": {
    color: "#0767d8",
    backgroundColor: "#dceeff",
    transform: "translateY(-1px)",
  },
};

const ESTILO_ACAO_EXCLUIR = {
  width: 30,
  height: 30,
  p: 0,
  color: "#e53935",
  backgroundColor: "#ffeded",
  border: "1px solid #ffdcdc",
  borderRadius: "50%",
  transition:
    "background-color 0.15s ease, color 0.15s ease, transform 0.15s ease",

  "&:hover": {
    color: "#c62828",
    backgroundColor: "#ffdddd",
    transform: "translateY(-1px)",
  },
};

const ESTILO_ACAO_DETALHES = {
  width: 30,
  height: 30,
  p: 0,
  color: "#087cf0",
  backgroundColor: "#eef7ff",
  border: "1px solid #d7eaff",
  borderRadius: "50%",
  transition:
    "background-color 0.15s ease, color 0.15s ease, transform 0.15s ease",

  "&:hover": {
    color: "#0767d8",
    backgroundColor: "#dceeff",
    transform: "translateY(-1px)",
  },
};

export default function ProcessosCriados() {
  // ====================================================
  // DADOS E MODAIS
  // ====================================================

  const [processos, setProcessos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [errorType, setErrorType] = useState(null);

  const [cadastroAberto, setCadastroAberto] = useState(false);
  const [processoParaEditar, setProcessoParaEditar] = useState(null);
  const [processoParaExcluir, setProcessoParaExcluir] = useState(null);
  const [excluindo, setExcluindo] = useState(false);

  // ====================================================
  // LISTAGEM
  // ====================================================

  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("todas");

  const [ordem, setOrdem] = useState({
    coluna: "nome",
    direcao: "asc",
  });

  // ====================================================
  // DETALHES DO PROCESSO
  // ====================================================

  const [open, setOpen] = useState(false);
  const [abaDetalhes, setAbaDetalhes] = useState(0);
  const [selectedProcesso, setSelectedProcesso] = useState(null);
  const [documentoDestacadoId, setDocumentoDestacadoId] = useState(null);
  const documentoDestacadoRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  // ====================================================
  // MENSAGENS
  // ====================================================

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ====================================================
  // CONSULTAR PROCESSOS
  // ====================================================

  useEffect(() => {
    const fetchProcessos = async () => {
      try {
        setIsLoading(true);

        const response = await fetch(api.adminSearchDocsProcess(), {
          credentials: "include",
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Erro ao buscar processos");
        }

        const data = await response.json();

        const processosFormatados = data.map((processo) => ({
          ...processo,

          // Temporário: a categoria ainda não vem do backend.
          categoriaVisual: "sem_categoria",

          quantidade: Array.isArray(processo.documentos)
            ? processo.documentos.length
            : 0,
        }));

        setProcessos(processosFormatados);
      } catch (error) {
        console.error("Erro ao listar processos:", error);

        if (error.message?.includes("Network Error")) {
          setErrorType("network");
        } else if (error.response?.status === 403) {
          setErrorType("permission");
        } else {
          setErrorType("module");
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchProcessos();
  }, [refreshKey]);

  // ====================================================
  // ABERTURA A PARTIR DE DOCUMENTOS
  // ====================================================

  useEffect(() => {
    const processoId = location.state?.processoIdParaVisualizar;

    if (!processoId || isLoading || errorType) {
      return;
    }

    const processo = processos.find(
      (item) => Number(item.id) === Number(processoId),
    );

    if (processo) {
      setSelectedProcesso(processo);
      setDocumentoDestacadoId(location.state.documentoIdParaDestacar ?? null);
      setAbaDetalhes(1);
      setOpen(true);
    } else {
      setSnackbar({
        open: true,
        message: "Processo vinculado não encontrado.",
        severity: "warning",
      });
    }

    navigate(location.pathname, {
      replace: true,
      state: null,
    });
  }, [location, isLoading, errorType, processos, navigate]);

  // ====================================================
  // POSICIONAR DOCUMENTO DESTACADO
  // ====================================================

  useEffect(() => {
    if (open && abaDetalhes === 1 && documentoDestacadoId !== null) {
      documentoDestacadoRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [open, abaDetalhes, selectedProcesso, documentoDestacadoId]);

  // ====================================================
  // ATUALIZAÇÃO APÓS CADASTRO E EDIÇÃO
  // ====================================================

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
    setPaginaAtual(1);
    setSearchTerm("");
    setFiltroCategoria("todas");
  };

  const handleProcessoCriado = () => {
    setCadastroAberto(false);

    setSnackbar({
      open: true,
      message: "Processo criado com sucesso!",
      severity: "success",
    });

    handleRefresh();
  };

  const handleProcessoAtualizado = () => {
    setProcessoParaEditar(null);

    setSnackbar({
      open: true,
      message: "Processo atualizado com sucesso!",
      severity: "success",
    });

    handleRefresh();
  };

  // ====================================================
  // FILTRO E ORDENAÇÃO
  // ====================================================

  const processosFiltrados = processos
    .filter((processo) => {
      const correspondeBusca = String(processo.nome || "")
        .toLocaleLowerCase("pt-BR")
        .includes(searchTerm.trim().toLocaleLowerCase("pt-BR"));

      const correspondeCategoria =
        filtroCategoria === "todas" ||
        processo.categoriaVisual === filtroCategoria;

      return correspondeBusca && correspondeCategoria;
    })
    .sort((a, b) => {
      const resultado =
        ordem.coluna === "quantidade"
          ? a.quantidade - b.quantidade
          : String(a[ordem.coluna] || "").localeCompare(
              String(b[ordem.coluna] || ""),
              "pt-BR",
              {
                sensitivity: "base",
                numeric: true,
              },
            );

      return ordem.direcao === "asc" ? resultado : -resultado;
    });

  const ordenarPor = (coluna) => {
    setOrdem((atual) => ({
      coluna,
      direcao:
        atual.coluna === coluna && atual.direcao === "asc" ? "desc" : "asc",
    }));

    setPaginaAtual(1);
  };

  // ====================================================
  // PAGINAÇÃO
  // ====================================================

  const totalPaginas = Math.max(
    1,
    Math.ceil(processosFiltrados.length / itensPorPagina),
  );

  const paginaExibida = Math.min(paginaAtual, totalPaginas);
  const indiceInicial = (paginaExibida - 1) * itensPorPagina;

  const indiceFinal = Math.min(
    indiceInicial + itensPorPagina,
    processosFiltrados.length,
  );

  const processosExibidos = processosFiltrados.slice(
    indiceInicial,
    indiceFinal,
  );

  // ====================================================
  // CABEÇALHOS ORDENÁVEIS
  // ====================================================

  const renderCabecalhoOrdenavel = (titulo, coluna, Icone) => {
    const ativo = ordem.coluna === coluna;

    return (
      <TableSortLabel
        active={ativo}
        direction={ativo ? ordem.direcao : "asc"}
        onClick={() => ordenarPor(coluna)}
        IconComponent={
          ativo
            ? ordem.direcao === "asc"
              ? ArrowUpward
              : ArrowDownward
            : UnfoldMore
        }
        sx={{
          color: "#27456f",
          fontSize: ajustarTamanhoFonteSre("0.75rem"),
          fontWeight: 700,
          "&:hover": {
            color: "#0767d8",
          },
          "&.Mui-active": {
            color: "#27456f",
          },
          "& .MuiTableSortLabel-icon": {
            opacity: 1,
            color: "#385b90",
            fontSize: 15,
            ml: 0.35,
            transform: "none !important",
          },
        }}
      >
        <Box
          component="span"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.8,
          }}
        >
          <Icone
            sx={{
              color: "#087cf0",
              fontSize: 17,
              flexShrink: 0,
            }}
          />
          {titulo}
        </Box>
      </TableSortLabel>
    );
  };

  // ====================================================
  // AÇÕES
  // ====================================================

  const handleEditar = (processo) => {
    setProcessoParaEditar(processo.id);
  };

  const handleVisualizar = (processo, abaInicial = 0) => {
    setDocumentoDestacadoId(null);
    setAbaDetalhes(abaInicial);
    setSelectedProcesso(processo);
    setOpen(true);
  };

  // Exclusão existente preservada.
  const handleDeletar = async (id) => {
    if (excluindo) return;

    setExcluindo(true);

    try {
      const response = await fetch(api.adminDeleteProcess(id), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Erro ao excluir processo");
      }

      setProcessoParaExcluir(null);

      setSnackbar({
        open: true,
        message: "Processo excluído com sucesso!",
        severity: "success",
      });

      handleRefresh();
    } catch (error) {
      console.error("Erro ao excluir processo:", error);

      setSnackbar({
        open: true,
        message: error.message || "Erro ao excluir processo",
        severity: "error",
      });
    } finally {
      setExcluindo(false);
    }
  };

  // ====================================================
  // CARREGAMENTO E ERRO
  // ====================================================

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (errorType) {
    return (
      <ErrorState
        errorType={errorType}
        onRetry={() => {
          setErrorType(null);
          setRefreshKey((prev) => prev + 1);
        }}
      />
    );
  }

  return (
    <Box
      component={motion.div}
      onUpdate={() => {}}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      sx={{ width: "100%", py: 0 }}
    >
      {/* ====================================================
          CABEÇALHO
      ==================================================== */}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: {
            xs: "flex-start",
            sm: "center",
          },
          flexDirection: {
            xs: "column",
            sm: "row",
          },
          gap: 1.5,
          mb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.4,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              borderRadius: 1.5,
              color: "#087cf0",
              backgroundColor: "#e8f4ff",
            }}
          >
            <FolderCopy sx={{ fontSize: 27 }} />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h1"
              sx={{
                color: "#0767d8",
                fontSize: {
                  xs: ajustarTamanhoFonteSre("1.35rem"),
                  md: ajustarTamanhoFonteSre("1.55rem"),
                },
                fontWeight: 700,
                lineHeight: 1.15,
              }}
            >
              Processos
            </Typography>

            <Typography
              sx={{
                mt: 0.3,
                color: "#7b8798",
                fontSize: ajustarTamanhoFonteSre("0.8rem"),
                lineHeight: 1.35,
              }}
            >
              Gerencie os tipos de processo disponíveis para a SRE.
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          size="small"
          startIcon={<AddCircleRounded sx={{ fontSize: "18px !important" }} />}
          onClick={() => setCadastroAberto(true)}
          sx={botaoAdicionar}
        >
          Adicionar processo
        </Button>
      </Box>

      {/* ====================================================
          BUSCA E REGISTROS POR PÁGINA
      ==================================================== */}

      <Box
        sx={{
          display: "flex",
          alignItems: {
            xs: "stretch",
            md: "center",
          },
          flexDirection: {
            xs: "column",
            md: "row",
          },
          gap: 1.25,
          px: 1.25,
          py: 0.55,
          mb: 0.5,
          border: "1px solid #d5e7f7",
          borderRadius: 1.5,
          backgroundColor: "#f7fbff",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            size="small"
            placeholder="Buscar processos..."
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setPaginaAtual(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search
                    sx={{
                      color: "#087cf0",
                      fontSize: 18,
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              width: {
                xs: "100%",
                md: 440,
              },
              maxWidth: "100%",
              "& .MuiOutlinedInput-root": {
                height: 34,
                backgroundColor: "#ffffff",
                borderRadius: 1,
                "& fieldset": {
                  borderColor: "#d7e0ea",
                },
                "&:hover fieldset": {
                  borderColor: "#a9c9e8",
                },
              },
              "& .MuiInputBase-input": {
                fontSize: ajustarTamanhoFonteSre("0.78rem"),
              },
            }}
          />
        </Box>

        {/* FILTRO VISUAL DE CATEGORIA */}

        {EXIBIR_CATEGORIA_EXPERIMENTAL && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                color: "#667b98",
                fontSize: ajustarTamanhoFonteSre("0.7rem"),
                whiteSpace: "nowrap",
              }}
            >
              Categoria:
            </Typography>

            <Select
              size="small"
              value={filtroCategoria}
              onChange={(event) => {
                setFiltroCategoria(event.target.value);
                setPaginaAtual(1);
              }}
              inputProps={{
                "aria-label": "Filtrar por categoria",
              }}
              sx={{
                minWidth: 155,
                height: 34,
                backgroundColor: "#ffffff",
                color: "#314d76",
                borderRadius: 1,
                fontSize: ajustarTamanhoFonteSre("0.72rem"),
              }}
            >
              <MenuItem value="todas">Todas</MenuItem>
              <MenuItem value="escola">Escola</MenuItem>
              <MenuItem value="mantenedora">Mantenedora</MenuItem>
              <MenuItem value="sem_categoria">Sem categoria</MenuItem>
            </Select>
          </Box>
        )}

        <Box
          sx={{
            display: {
              xs: "none",
              md: "block",
            },
            width: "1px",
            height: 30,
            flexShrink: 0,
            backgroundColor: "#d8e7f4",
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: {
              xs: "space-between",
              md: "flex-end",
            },
            gap: 1.2,
            flexShrink: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#667b98",
              fontSize: ajustarTamanhoFonteSre("0.7rem"),
              whiteSpace: "nowrap",
            }}
          >
            Registros por página:
          </Typography>

          <Select
            size="small"
            value={itensPorPagina}
            onChange={(event) => {
              setItensPorPagina(Number(event.target.value));
              setPaginaAtual(1);
            }}
            renderValue={(valor) => `${valor} por página`}
            sx={{
              width: 160,
              height: 34,
              backgroundColor: "#ffffff",
              color: "#314d76",
              borderRadius: 1,
              fontSize: ajustarTamanhoFonteSre("0.72rem"),
              fontWeight: 500,
              "& .MuiSelect-select": {
                py: 0.55,
              },
            }}
          >
            {OPCOES_ITENS_POR_PAGINA.map((quantidade) => (
              <MenuItem key={quantidade} value={quantidade}>
                {quantidade} por página
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {/* ====================================================
          PAGINAÇÃO SUPERIOR
      ==================================================== */}

      {processosFiltrados.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: {
              xs: "flex-start",
              sm: "center",
            },
            justifyContent: "space-between",
            flexDirection: {
              xs: "column",
              sm: "row",
            },
            gap: 0.75,
            px: 1.5,
            py: 0.35,
            mb: 0.5,
            border: "1px solid #d7e8f8",
            borderRadius: 1.5,
            backgroundColor: "#f2f8fe",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#536b8b",
              fontSize: ajustarTamanhoFonteSre("0.72rem"),
            }}
          >
            {processosFiltrados.length}{" "}
            {processosFiltrados.length === 1 ? "registro" : "registros"}
          </Typography>

          <Box sx={{ ml: { sm: "auto" } }}>
            <PaginacaoLista
              paginaAtual={paginaExibida}
              totalPaginas={totalPaginas}
              onChange={setPaginaAtual}
              ariaLabel="Paginação superior da lista de processos"
            />
          </Box>
        </Box>
      )}

      {/* ====================================================
          TABELA DE PROCESSOS
      ==================================================== */}

      {processosFiltrados.length === 0 ? (
        <Paper
          sx={{
            minHeight: 250,
            border: "1px solid #d9e8fa",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: 2,
            py: 4,
            boxShadow: "none",
          }}
        >
          <Box
            sx={{
              width: 70,
              height: 70,
              borderRadius: "50%",
              backgroundColor: "#eaf4ff",
              display: "grid",
              placeItems: "center",
              mb: 1.5,
            }}
          >
            <FolderCopy
              sx={{
                color: "#087cf0",
                fontSize: 38,
              }}
            />
          </Box>

          <Typography
            sx={{
              color: "#0767d8",
              fontSize: ajustarTamanhoFonteSre("1.1rem"),
              fontWeight: 700,
            }}
          >
            {searchTerm.trim() || filtroCategoria !== "todas"
              ? "Nenhum processo encontrado"
              : "Nenhum processo cadastrado"}
          </Typography>

          <Typography
            sx={{
              color: "text.secondary",
              maxWidth: 390,
              mt: 0.5,
              mb: 2,
              fontSize: ajustarTamanhoFonteSre("0.85rem"),
            }}
          >
            {searchTerm.trim()
              ? "Nenhum processo corresponde à sua busca."
              : filtroCategoria !== "todas"
                ? "Nenhum processo corresponde à categoria selecionada."
                : "Ainda não há processos cadastrados."}
          </Typography>

          {!searchTerm.trim() && filtroCategoria === "todas" && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddCircleRounded />}
              onClick={() => setCadastroAberto(true)}
              sx={botaoAdicionar}
            >
              Criar primeiro processo
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 1.5,
            border: "1px solid #d7e7f7",
            boxShadow: "none",
            overflowX: "auto",
          }}
        >
          <Table
            size="small"
            sx={{
              minWidth: EXIBIR_CATEGORIA_EXPERIMENTAL ? 1000 : 820,
              tableLayout: "fixed",
              "& .MuiTableCell-root": {
                borderColor: "#dfe9f3",
              },
            }}
          >
            <TableHead
              sx={{
                backgroundColor: "#edf6fe",
              }}
            >
              <TableRow>
                <TableCell
                  sx={{
                    width: EXIBIR_CATEGORIA_EXPERIMENTAL ? "26%" : "32%",
                    px: 2,
                    py: 0.55,
                    borderRight: "1px solid #d9e7f3",
                  }}
                >
                  {renderCabecalhoOrdenavel("Processo", "nome", FolderCopy)}
                </TableCell>

                {/* CATEGORIA */}

                {EXIBIR_CATEGORIA_EXPERIMENTAL && (
                  <TableCell
                    sx={{
                      width: "16%",
                      px: 1,
                      py: 0.55,
                      borderRight: "1px solid #d9e7f3",
                    }}
                  >
                    {renderCabecalhoOrdenavel(
                      "Categoria",
                      "categoriaVisual",
                      GroupsRounded,
                    )}
                  </TableCell>
                )}

                <TableCell
                  sx={{
                    width: EXIBIR_CATEGORIA_EXPERIMENTAL ? "28%" : "38%",
                    px: 2,
                    py: 0.55,
                    borderRight: "1px solid #d9e7f3",
                  }}
                >
                  {renderCabecalhoOrdenavel(
                    "Descrição",
                    "descricao",
                    DescriptionRounded,
                  )}
                </TableCell>

                <TableCell
                  sx={{
                    width: "14%",
                    px: 1,
                    py: 0.55,
                    borderRight: "1px solid #d9e7f3",
                  }}
                >
                  {renderCabecalhoOrdenavel(
                    "Documentos",
                    "quantidade",
                    DescriptionRounded,
                  )}
                </TableCell>

                <TableCell
                  align="center"
                  sx={{
                    width: "16%",
                    px: 1,
                    py: 0.55,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.7,
                    }}
                  >
                    <SettingsRounded
                      sx={{
                        color: "#087cf0",
                        fontSize: 17,
                      }}
                    />

                    <Typography
                      variant="caption"
                      sx={{
                        color: "#27456f",
                        fontSize: ajustarTamanhoFonteSre("0.75rem"),
                        fontWeight: 700,
                      }}
                    >
                      Ações
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {processosExibidos.map((processo) => (
                <TableRow
                  key={processo.id}
                  hover
                  sx={{
                    height: 38,
                    "&:hover": {
                      backgroundColor: "#fbfdff",
                    },
                    "&:last-child td": {
                      borderBottom: 0,
                    },
                  }}
                >
                  {/* PROCESSO */}

                  <TableCell
                    sx={{
                      px: 2,
                      py: 0.5,
                      borderRight: "1px solid #e5edf5",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.2,
                        minWidth: 0,
                      }}
                    >
                      <FolderCopy
                        sx={{
                          color: "#087cf0",
                          fontSize: 18,
                          flexShrink: 0,
                        }}
                      />

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          title={processo.nome || "Processo sem nome"}
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: "#0767d8",
                            fontSize: ajustarTamanhoFonteSre("0.78rem"),
                            fontWeight: 600,
                            textTransform: "uppercase",
                          }}
                        >
                          {processo.nome || "Processo sem nome"}
                        </Typography>

                        {processo.para_todas_escolas && (
                          <Chip
                            label="Todas escolas"
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{
                              mt: 0.4,
                              height: 20,
                              fontSize: ajustarTamanhoFonteSre("0.66rem"),
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </TableCell>

                  {/* CATEGORIA */}

                  {EXIBIR_CATEGORIA_EXPERIMENTAL && (
                    <TableCell
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRight: "1px solid #e5edf5",
                      }}
                    >
                      <Chip
                        icon={
                          processo.categoriaVisual === "escola" ? (
                            <SchoolRounded sx={{ fontSize: 16 }} />
                          ) : processo.categoriaVisual === "mantenedora" ? (
                            <BusinessRounded sx={{ fontSize: 16 }} />
                          ) : undefined
                        }
                        label={
                          ROTULOS_CATEGORIA[processo.categoriaVisual] ||
                          ROTULOS_CATEGORIA.sem_categoria
                        }
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 24,
                          maxWidth: "100%",
                          borderColor: "#d7e2ed",
                          backgroundColor: "#f7f9fc",
                          color: "#64748b",
                          fontSize: ajustarTamanhoFonteSre("0.7rem"),
                        }}
                      />
                    </TableCell>
                  )}

                  {/* DESCRIÇÃO */}

                  <TableCell
                    sx={{
                      px: 2,
                      py: 0.5,
                      borderRight: "1px solid #e5edf5",
                    }}
                  >
                    <Typography
                      title={
                        processo.descricao || "Nenhuma descrição fornecida."
                      }
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: "#40597c",
                        fontSize: ajustarTamanhoFonteSre("0.77rem"),
                      }}
                    >
                      {processo.descricao || "Nenhuma descrição fornecida."}
                    </Typography>
                  </TableCell>

                  {/* CONTADOR DE DOCUMENTOS */}

                  <TableCell
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRight: "1px solid #e5edf5",
                    }}
                  >
                    <Chip
                      label={`${processo.quantidade} documento${
                        processo.quantidade !== 1 ? "s" : ""
                      }`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      onClick={() => handleVisualizar(processo, 1)}
                      aria-label={`Visualizar documentos vinculados ao processo ${processo.nome}`}
                      sx={{
                        height: 24,
                        fontSize: ajustarTamanhoFonteSre("0.7rem"),
                        maxWidth: "100%",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "#eaf4ff",
                        },
                      }}
                    />
                  </TableCell>

                  {/* AÇÕES */}

                  <TableCell
                    align="center"
                    sx={{
                      px: 1,
                      py: 0.5,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 0.85,
                      }}
                    >
                      <Tooltip title="Editar" arrow>
                        <IconButton
                          size="small"
                          aria-label={`Editar ${processo.nome || "processo"}`}
                          onClick={() => handleEditar(processo)}
                          sx={ESTILO_ACAO_EDITAR}
                        >
                          <EditRounded sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Excluir" arrow>
                        <IconButton
                          size="small"
                          aria-label={`Excluir ${processo.nome || "processo"}`}
                          onClick={() => setProcessoParaExcluir(processo)}
                          sx={ESTILO_ACAO_EXCLUIR}
                        >
                          <DeleteRounded sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Visualizar" arrow>
                        <IconButton
                          size="small"
                          aria-label={`Visualizar ${
                            processo.nome || "processo"
                          }`}
                          onClick={() => handleVisualizar(processo)}
                          sx={ESTILO_ACAO_DETALHES}
                        >
                          <VisibilityRounded sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ====================================================
          PAGINAÇÃO INFERIOR
      ==================================================== */}

      {processosFiltrados.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: {
              xs: "flex-start",
              sm: "center",
            },
            justifyContent: "space-between",
            flexDirection: {
              xs: "column",
              sm: "row",
            },
            gap: 0.75,
            mt: 0.45,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#526b8d",
              fontSize: ajustarTamanhoFonteSre("0.72rem"),
            }}
          >
            {`Mostrando ${indiceInicial + 1} a ${indiceFinal} de ${
              processosFiltrados.length
            } resultados`}
          </Typography>

          <Box sx={{ ml: { sm: "auto" } }}>
            <PaginacaoLista
              paginaAtual={paginaExibida}
              totalPaginas={totalPaginas}
              onChange={setPaginaAtual}
              ariaLabel="Paginação inferior da lista de processos"
            />
          </Box>
        </Box>
      )}

      {/* ====================================================
          CONFIRMAÇÃO DE EXCLUSÃO
      ==================================================== */}

      <Dialog
        open={Boolean(processoParaExcluir)}
        onClose={() => {
          if (!excluindo) {
            setProcessoParaExcluir(null);
          }
        }}
        fullWidth
        maxWidth="xs"
        aria-labelledby="titulo-excluir-processo"
        PaperProps={{
          sx: {
            borderRadius: 2,
            borderTop: "4px solid #e53935",
          },
        }}
      >
        <DialogTitle
          id="titulo-excluir-processo"
          sx={{
            color: "#c62828",
            fontSize: ajustarTamanhoFonteSre("1.05rem"),
            fontWeight: 700,
          }}
        >
          Excluir processo?
        </DialogTitle>

        <DialogContent>
          <Typography
            sx={{
              color: "#40546d",
              fontSize: ajustarTamanhoFonteSre("0.84rem"),
              lineHeight: 1.6,
            }}
          >
            Tem certeza que deseja excluir o processo{" "}
            <strong>{processoParaExcluir?.nome}</strong>?
          </Typography>

          <Typography
            sx={{
              mt: 1,
              color: "#c62828",
              fontSize: ajustarTamanhoFonteSre("0.77rem"),
            }}
          >
            Esta ação não poderá ser desfeita.
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: "1px solid #e5edf5",
            justifyContent: "space-between",
          }}
        >
          <Button
            variant="outlined"
            disabled={excluindo}
            onClick={() => setProcessoParaExcluir(null)}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            color="error"
            disabled={excluindo}
            startIcon={
              excluindo ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteRounded />
              )
            }
            onClick={() => {
              if (processoParaExcluir) {
                handleDeletar(processoParaExcluir.id);
              }
            }}
          >
            {excluindo ? "Excluindo..." : "Excluir processo"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ====================================================
          MODAL DE CRIAÇÃO
      ==================================================== */}

      {cadastroAberto && (
        <CriarProcesso
          onCancelar={() => setCadastroAberto(false)}
          onCriado={handleProcessoCriado}
        />
      )}

      {/* ====================================================
          MODAL DE EDIÇÃO
      ==================================================== */}

      {processoParaEditar !== null && (
        <CriarProcesso
          modo="editar"
          processoId={processoParaEditar}
          onCancelar={() => setProcessoParaEditar(null)}
          onAtualizado={handleProcessoAtualizado}
        />
      )}

      {/* ====================================================
          MODAL DE DETALHES
      ==================================================== */}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            width: {
              xs: "calc(100% - 24px)",
              sm: 600,
            },
            maxWidth: "600px !important",
            overflow: "hidden",
            borderRadius: 2.5,
            border: "1px solid #d7e7f7",
            borderTop: "5px solid #087cf0",
            boxShadow: "0 18px 48px rgba(20, 61, 106, 0.18)",
          },
        }}
      >
        <DialogTitle
          sx={{
            position: "relative",
            px: {
              xs: 2.25,
              sm: 3,
            },
            pt: 2.25,
            pb: 1.65,
            pr: 7,
            borderBottom: "1px solid #e6edf4",
            backgroundColor: "#ffffff",
          }}
        >
          <Typography
            component="h2"
            sx={{
              color: "#0767d8",
              fontSize: {
                xs: ajustarTamanhoFonteSre("1.35rem"),
                sm: ajustarTamanhoFonteSre("1.5rem"),
              },
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            Detalhes do processo
          </Typography>

          <IconButton
            aria-label="Fechar"
            onClick={() => setOpen(false)}
            sx={{
              position: "absolute",
              right: 14,
              top: 13,
              color: "#53657a",
              "&:hover": {
                color: "#0767d8",
                backgroundColor: "#eef6ff",
              },
            }}
          >
            <CloseRounded />
          </IconButton>
        </DialogTitle>

        <Tabs
          value={abaDetalhes}
          onChange={(_, novaAba) => setAbaDetalhes(novaAba)}
          variant="fullWidth"
          aria-label="Abas dos detalhes do processo"
          sx={{
            borderBottom: "1px solid #e6edf4",
            backgroundColor: "#ffffff",
            minHeight: 44,
            "& .MuiTab-root": {
              minHeight: 44,
              py: 1,
              px: 1,
              color: "#61758c",
              fontSize: ajustarTamanhoFonteSre("0.78rem"),
              fontWeight: 600,
              textTransform: "none",
            },
            "& .MuiTab-root.Mui-selected": {
              color: "#0767d8",
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#087cf0",
              height: 3,
            },
          }}
        >
          <Tab
            icon={<FolderCopy sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Dados do processo"
            id="aba-processo-dados"
            aria-controls="painel-processo-dados"
          />

          <Tab
            icon={<DescriptionRounded sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Documentos vinculados"
            id="aba-processo-documentos"
            aria-controls="painel-processo-documentos"
          />
        </Tabs>

        <DialogContent
          sx={{
            px: {
              xs: 2,
              sm: 2.5,
            },
            py: 2.2,
            backgroundColor: "#fbfdff",
          }}
        >
          {abaDetalhes === 0 ? (
            <Box
              id="painel-processo-dados"
              role="tabpanel"
              aria-labelledby="aba-processo-dados"
            >
              {/* IDENTIFICAÇÃO */}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.35,
                  px: 1.5,
                  py: 1.25,
                  mb: 2,
                  border: "1px solid #dceaf7",
                  borderRadius: 1.7,
                  backgroundColor: "#f3f8fd",
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    borderRadius: 1.3,
                    color: "#087cf0",
                    backgroundColor: "#e3f2ff",
                  }}
                >
                  <FolderCopy sx={{ fontSize: 23 }} />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: "#164f91",
                      fontSize: ajustarTamanhoFonteSre("0.88rem"),
                      fontWeight: 700,
                      lineHeight: 1.25,
                    }}
                  >
                    Dados do processo
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.15,
                      color: "#71849d",
                      fontSize: ajustarTamanhoFonteSre("0.74rem"),
                      lineHeight: 1.4,
                    }}
                  >
                    Informações cadastradas para este tipo de processo.
                  </Typography>
                </Box>
              </Box>

              {/* TIPO DE INSTITUIÇÃO */}

              {EXIBIR_CATEGORIA_EXPERIMENTAL && (
                <Box sx={{ mb: 1.7 }}>
                  <Typography
                    sx={{
                      mb: 0.55,
                      color: "#314967",
                      fontSize: ajustarTamanhoFonteSre("0.76rem"),
                      fontWeight: 700,
                      lineHeight: 1.3,
                    }}
                  >
                    Tipo de instituição
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minHeight: 44,
                      px: 1.35,
                      py: 1,
                      border: "1px solid #d7e2ed",
                      borderRadius: 1.3,
                      backgroundColor: "#ffffff",
                    }}
                  >
                    {selectedProcesso?.categoriaVisual === "escola" ? (
                      <SchoolRounded
                        sx={{
                          color: "#087cf0",
                          fontSize: 19,
                        }}
                      />
                    ) : selectedProcesso?.categoriaVisual === "mantenedora" ? (
                      <BusinessRounded
                        sx={{
                          color: "#087cf0",
                          fontSize: 19,
                        }}
                      />
                    ) : (
                      <GroupsRounded
                        sx={{
                          color: "#607995",
                          fontSize: 19,
                        }}
                      />
                    )}

                    <Typography
                      sx={{
                        color: "#173f72",
                        fontSize: ajustarTamanhoFonteSre("0.8rem"),
                        fontWeight: 700,
                      }}
                    >
                      {ROTULOS_CATEGORIA[selectedProcesso?.categoriaVisual] ||
                        ROTULOS_CATEGORIA.sem_categoria}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* NOME */}

              <Box sx={{ mb: 1.7 }}>
                <Typography
                  sx={{
                    mb: 0.55,
                    color: "#314967",
                    fontSize: ajustarTamanhoFonteSre("0.76rem"),
                    fontWeight: 700,
                    lineHeight: 1.3,
                  }}
                >
                  Nome do processo
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minHeight: 44,
                    px: 1.35,
                    py: 1,
                    border: "1px solid #d7e2ed",
                    borderRadius: 1.3,
                    backgroundColor: "#ffffff",
                  }}
                >
                  <FolderCopy
                    sx={{
                      color: "#607995",
                      fontSize: 19,
                      flexShrink: 0,
                    }}
                  />

                  <Typography
                    sx={{
                      color: "#173f72",
                      fontSize: ajustarTamanhoFonteSre("0.8rem"),
                      fontWeight: 700,
                      lineHeight: 1.45,
                      textTransform: "uppercase",
                      wordBreak: "break-word",
                    }}
                  >
                    {selectedProcesso?.nome || "Processo sem nome"}
                  </Typography>
                </Box>
              </Box>

              {/* DESCRIÇÃO */}

              <Box>
                <Typography
                  sx={{
                    mb: 0.55,
                    color: "#314967",
                    fontSize: ajustarTamanhoFonteSre("0.76rem"),
                    fontWeight: 700,
                    lineHeight: 1.3,
                  }}
                >
                  Descrição
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    minHeight: 92,
                    px: 1.35,
                    py: 1.15,
                    border: "1px solid #d7e2ed",
                    borderRadius: 1.3,
                    backgroundColor: "#ffffff",
                  }}
                >
                  <SubjectRounded
                    sx={{
                      color: "#607995",
                      fontSize: 19,
                      flexShrink: 0,
                      mt: 0.1,
                    }}
                  />

                  <Typography
                    sx={{
                      color: "#40546d",
                      fontSize: ajustarTamanhoFonteSre("0.79rem"),
                      lineHeight: 1.55,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {selectedProcesso?.descricao ||
                      "Nenhuma descrição fornecida."}
                  </Typography>
                </Box>
              </Box>

              {selectedProcesso?.para_todas_escolas && (
                <Box sx={{ mt: 1.5 }}>
                  <Chip
                    label="Aplicável a todas as escolas"
                    color="success"
                    variant="outlined"
                    size="small"
                  />
                </Box>
              )}
            </Box>
          ) : (
            <Box
              id="painel-processo-documentos"
              role="tabpanel"
              aria-labelledby="aba-processo-documentos"
            >
              {/* IDENTIFICAÇÃO */}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.35,
                  px: 1.5,
                  py: 1.25,
                  mb: 2,
                  border: "1px solid #dceaf7",
                  borderRadius: 1.7,
                  backgroundColor: "#f3f8fd",
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    borderRadius: 1.3,
                    color: "#087cf0",
                    backgroundColor: "#e3f2ff",
                  }}
                >
                  <DescriptionRounded sx={{ fontSize: 23 }} />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: "#164f91",
                      fontSize: ajustarTamanhoFonteSre("0.88rem"),
                      fontWeight: 700,
                      lineHeight: 1.25,
                    }}
                  >
                    Documentos vinculados
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.15,
                      color: "#71849d",
                      fontSize: ajustarTamanhoFonteSre("0.74rem"),
                      lineHeight: 1.4,
                    }}
                  >
                    Documentos exigidos por este tipo de processo.
                  </Typography>
                </Box>
              </Box>

              {!Array.isArray(selectedProcesso?.documentos) ||
              selectedProcesso.documentos.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.2,
                    px: 1.5,
                    py: 1.4,
                    border: "1px solid #d8e3ed",
                    borderRadius: 1.5,
                    backgroundColor: "#f7f9fb",
                  }}
                >
                  <InfoRounded
                    sx={{
                      color: "#607995",
                      fontSize: 22,
                      flexShrink: 0,
                    }}
                  />

                  <Box>
                    <Typography
                      sx={{
                        color: "#314967",
                        fontSize: ajustarTamanhoFonteSre("0.8rem"),
                        fontWeight: 700,
                      }}
                    >
                      Nenhum documento vinculado encontrado.
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.4,
                        color: "#60748d",
                        fontSize: ajustarTamanhoFonteSre("0.76rem"),
                        lineHeight: 1.5,
                      }}
                    >
                      Este processo não possui documentos cadastrados em sua
                      relação.
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.4,
                      py: 1,
                      mb: 1.1,
                      border: "1px solid #d8e3ed",
                      borderRadius: 1.5,
                      backgroundColor: "#f7f9fb",
                    }}
                  >
                    <InfoRounded
                      sx={{
                        color: "#527295",
                        fontSize: 20,
                      }}
                    />

                    <Typography
                      sx={{
                        color: "#52667e",
                        fontSize: ajustarTamanhoFonteSre("0.77rem"),
                      }}
                    >
                      Este processo possui{" "}
                      <strong>
                        {selectedProcesso.documentos.length}{" "}
                        {selectedProcesso.documentos.length === 1
                          ? "documento vinculado"
                          : "documentos vinculados"}
                      </strong>
                      .
                    </Typography>
                  </Box>

                  {/* LISTA DE DOCUMENTOS */}

                  <Box
                    component="ul"
                    sx={{
                      listStyle: "none",
                      m: 0,
                      p: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.85,
                      maxHeight: 260,
                      overflowY: "auto",
                      pr: 0.25,
                    }}
                  >
                    {selectedProcesso.documentos.map((documento) => {
                      const destacado =
                        documentoDestacadoId !== null &&
                        Number(documento.id) === Number(documentoDestacadoId);

                      return (
                        <Box
                          component="li"
                          key={documento.id}
                          ref={destacado ? documentoDestacadoRef : null}
                          sx={{ listStyle: "none" }}
                        >
                          <Box
                            component={RouterLink}
                            to="/admin/cadastro-documento"
                            state={{
                              documentoIdParaVisualizar: documento.id,
                              processoIdParaDestacar: selectedProcesso.id,
                            }}
                            aria-label={`Visualizar documento ${
                              documento.nome || documento.id
                            }`}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1.1,
                              px: 1.35,
                              py: 1.15,
                              border: destacado
                                ? "2px solid #087cf0"
                                : "1px solid #d7e8f8",
                              borderLeft: "4px solid #087cf0",
                              borderRadius: 1.4,
                              backgroundColor: destacado
                                ? "#eaf4ff"
                                : "#f2f8fe",
                              textDecoration: "none",
                              cursor: "pointer",
                              "&:hover": {
                                filter: "brightness(0.97)",
                              },
                              "&:focus-visible": {
                                outline: "2px solid #087cf0",
                                outlineOffset: 2,
                              },
                            }}
                          >
                            <DescriptionRounded
                              sx={{
                                color: "#087cf0",
                                fontSize: 21,
                                flexShrink: 0,
                                mt: 0.1,
                              }}
                            />

                            <Typography
                              sx={{
                                color: "#173a67",
                                fontSize: ajustarTamanhoFonteSre("0.78rem"),
                                fontWeight: 700,
                                lineHeight: 1.45,
                                wordBreak: "break-word",
                              }}
                            >
                              {documento.nome || "Documento sem nome"}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.45,
            borderTop: "1px solid #e5ebf1",
            backgroundColor: "#ffffff",
          }}
        >
          <Button
            onClick={() => setOpen(false)}
            variant="contained"
            sx={{
              borderRadius: 1.5,
              backgroundColor: "#087cf0",
              "&:hover": {
                backgroundColor: "#0767d8",
              },
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ====================================================
          FEEDBACK
      ==================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() =>
          setSnackbar({
            ...snackbar,
            open: false,
          })
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={() =>
            setSnackbar({
              ...snackbar,
              open: false,
            })
          }
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
